import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import QRCodeStyling from 'qr-code-styling';
import s from './NovaCommissionModal.module.sass';
import { Copy, Check, Eye, EyeOff, ScanLine, ShieldAlert, Landmark } from 'lucide-react';
import BankLogo from '../components/BankLogo';

/**
 * Общие строительные блоки платёжных модалок Nova (комиссия, верификация…):
 * фирменный QR, суммы с приподнятыми копейками, блок «адрес + сеть»
 * с умным усечением. Все стили — из общего модуля NovaCommissionModal.
 */

// Клик по оверлею закрывает модал — кроме клика, «израсходованного» админкой:
// при открытой админке (с включённым автоскрытием) первый клик в приложение
// закрывает только её (метку ставит AdminPanel), а модал остаётся —
// его закроет следующий клик
export const closeUnlessAdminConsumed = (onClose) => () => {
    if (document.documentElement.hasAttribute('data-admin-consumed-click')) return;
    onClose();
};

// Крипто-адрес — одной строкой без переносов: перенесённый адрес выглядит
// «сломанным» и его неудобно сверять. Вмещаем максимум символов, а если не
// влезает — усекаем по середине (0x29…9f34): начало и конец адреса — именно
// те части, по которым его сверяют. Копирование и QR всегда с полным адресом.
//
// Ничего не вычисляем и не угадываем: ширина блока «адрес + сеть» задана
// в CSS нижним язычком (поле адреса имеет width: 0 / min-width: 100% и
// в расчёте ширины не участвует). JS только усекает текст под фактическую
// ширину <code> — а она от содержимого не зависит по построению,
// так что петля обратной связи невозможна
export function useMiddleTruncate(text) {
    const codeRef = useRef(null);
    const [display, setDisplay] = useState(text);

    useLayoutEffect(() => {
        const code = codeRef.current;
        if (!code) return undefined;
        const ctx = document.createElement('canvas').getContext('2d');

        const fit = () => {
            const full = String(text ?? '');
            const cs = getComputedStyle(code);
            ctx.font = `${cs.fontStyle} ${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`;
            // −2px страховки: canvas меряет чуть иначе, чем реальный рендер
            const available = code.clientWidth - 2;
            if (available <= 0) return;

            if (ctx.measureText(full).width <= available) {
                setDisplay(full);
                return;
            }
            let lo = 3;
            let hi = Math.floor((full.length - 1) / 2);
            let best = lo;
            while (lo <= hi) {
                const mid = (lo + hi) >> 1;
                const candidate = `${full.slice(0, mid)}…${full.slice(-mid)}`;
                if (ctx.measureText(candidate).width <= available) {
                    best = mid;
                    lo = mid + 1;
                } else {
                    hi = mid - 1;
                }
            }
            setDisplay(`${full.slice(0, best)}…${full.slice(-best)}`);
        };

        fit();
        const ro = new ResizeObserver(fit);
        ro.observe(code);
        return () => ro.disconnect();
    }, [text]);

    return [codeRef, display];
}

// Символ валюты отделяем от числа, чтобы набрать его мельче и приглушённее:
// в крупных суммах читается сама цифра, префикс — служебный. Регулярка берёт
// ведущие нецифровые символы, поэтому пробел-разделитель разрядов не мешает.
const splitAmount = (text) => {
    const raw = String(text ?? '');
    const match = raw.match(/^([^\d]*[^\d\s])\s*(.+)$/);
    return match ? { currency: match[1], value: match[2] } : { currency: '', value: raw };
};

// Копейки отделяем от целой части — как на дэшборде и в кошельке: мельче
// и приподняты к верху цифр. Разделитель зависит от numberFormat, поэтому
// берём последний из «,» / «.» и только при ровно двух знаках после него.
// Сам разделитель выбрасываем: приподнятые копейки уже отделены визуально
const splitCents = (value) => {
    const str = String(value || '');
    const cut = Math.max(str.lastIndexOf(','), str.lastIndexOf('.'));
    if (cut < 0) return [str, null];
    const cents = str.slice(cut + 1);
    return cents.length === 2 ? [str.slice(0, cut), cents] : [str, null];
};

// Режим темы читаем с DOM-атрибута, а не из контекста: модалка приходит
// порталом из App.js и может оказаться вне провайдера NovaThemeContext.
// data-nova-mode — единственный источник правды, следим за ним наблюдателем
export function useNovaMode() {
    const [mode, setMode] = useState('dark');

    useEffect(() => {
        const el = document.querySelector('[data-nova-mode]');
        if (!el) return undefined;
        const read = () => setMode(el.getAttribute('data-nova-mode') || 'dark');
        read();
        const observer = new MutationObserver(read);
        observer.observe(el, { attributes: true, attributeFilter: ['data-nova-mode'] });
        return () => observer.disconnect();
    }, []);

    return mode;
}

// QR в фирменном стиле вместо дефолтной решётки: скруглённые модули
// и акцентовые «глаза». Палитра следует режиму темы: в тёмном — светлые
// модули (инверсный код; современные камеры его читают), в светлом —
// классика: тёмные модули. SVG — чтобы не мылился на ретине
const QR_PALETTES = {
    dark: { dots: '#EDF0F8', eyes: '#9095FF' },
    light: { dots: '#161A23', eyes: '#5B60E8' },
};

export function NovaQr({ value, size }) {
    const hostRef = useRef(null);
    const qrRef = useRef(null);
    const mode = useNovaMode();
    const palette = QR_PALETTES[mode] || QR_PALETTES.dark;

    useEffect(() => {
        if (!hostRef.current) return;
        const options = {
            width: size,
            height: size,
            data: value,
            margin: 0,
            qrOptions: { errorCorrectionLevel: 'M' },
            dotsOptions: { type: 'extra-rounded', color: palette.dots },
            cornersSquareOptions: { type: 'extra-rounded', color: palette.eyes },
            cornersDotOptions: { type: 'dot', color: palette.eyes },
            backgroundOptions: { color: 'transparent' },
        };
        if (!qrRef.current) {
            qrRef.current = new QRCodeStyling({ type: 'svg', ...options });
            qrRef.current.append(hostRef.current);
        } else {
            qrRef.current.update(options);
        }
    }, [value, size, palette]);

    return <span ref={hostRef} className={s.qrCanvas} aria-hidden="true" />;
}

const AMOUNT_TONES = {
    out: s.amountOut,
    pending: s.amountPending,
    // Верификационный платёж — акцентный индиго: это проверка, а не потеря
    verify: s.amountVerify,
    // Комиссия за конвертацию — тёплый янтарный: плата за обработку
    fee: s.amountFee,
};

export function Amount({ text, tone }) {
    const { currency, value } = splitAmount(text);
    const [whole, cents] = splitCents(value);
    return (
        <span className={`${s.amount} ${AMOUNT_TONES[tone] || s.amountPending}`}>
            {currency && <span className={s.amountCur}>{currency}</span>}
            <span className={s.amountVal}>
                {whole}
                {cents !== null && <span className={s.amountCents}>{cents}</span>}
            </span>
        </span>
    );
}

// Колонка «язычок-заголовок → поле адреса → язычок сети»: единый блок,
// ширину которого задаёт нижняя полоса с сетью (см. .addrUnit в sass)
export function AddressUnit({
    safeT,
    paymentTitle,
    addressDisplay,
    networkInfoHtml,
    canHideAddress,
    isAddressHidden,
    onToggleAddressHidden,
    isCopied,
    onCopy,
}) {
    const masked = canHideAddress && isAddressHidden;

    // Мягкое усечение по середине с фиксированным бюджетом символов:
    // начало и конец адреса (по ним его сверяют) остаются длинными,
    // «…» съедает только середину. Копирование и QR — всегда полный адрес
    const HEAD = 15;
    const TAIL = 12;
    const full = String(addressDisplay ?? '');
    const addressShort = full.length > HEAD + TAIL + 3
        ? `${full.slice(0, HEAD)}…${full.slice(-TAIL)}`
        : full;

    return (
        <div className={s.depositMain}>
            {/* Язычок-заголовок, приклеенный к верху поля адреса.
                Если заголовок уже дан контекстом (например, шагом процесса) —
                paymentTitle не передают, и поле остаётся без язычка */}
            {paymentTitle && (
                <span className={`${s.eyebrow} ${s.tongueTop}`}>
                    <ScanLine size={11} strokeWidth={2.4} aria-hidden="true" />
                    {paymentTitle}
                </span>
            )}

            <div className={s.addrUnit}>
                <div className={s.addressBox}>
                    <code
                        className={`${s.address} ${masked ? s.addressMasked : ''}`}
                        title={masked ? undefined : addressDisplay}
                    >
                        {addressShort}
                    </code>
                    {canHideAddress ? (
                        <button
                            type="button"
                            className={s.addressBtn}
                            onClick={onToggleAddressHidden}
                            aria-label={isAddressHidden ? 'Show' : 'Hide'}
                        >
                            {isAddressHidden
                                ? <Eye size={14} strokeWidth={2} aria-hidden="true" />
                                : <EyeOff size={14} strokeWidth={2} aria-hidden="true" />}
                        </button>
                    ) : (
                        <button
                            type="button"
                            className={`${s.addressBtn} ${isCopied ? s.addressBtnCopied : ''}`}
                            onClick={onCopy}
                        >
                            {isCopied
                                ? <Check size={14} strokeWidth={2.4} aria-hidden="true" />
                                : <Copy size={14} strokeWidth={2} aria-hidden="true" />}
                            {/* Обе подписи стопкой в одной ячейке: невидимая
                                резервирует место, ширина кнопки постоянна —
                                бюджет адреса не плывёт при копировании */}
                            <span className={s.btnLabelStack}>
                                <span className={isCopied ? undefined : s.btnLabelGhost}>
                                    {safeT('modals.transactionError.buttons.copied', 'Disalin')}
                                </span>
                                <span className={isCopied ? s.btnLabelGhost : undefined}>
                                    {safeT('modals.tax.buttons.copy', 'Salin')}
                                </span>
                            </span>
                        </button>
                    )}
                </div>

                {/* Сеть — критичный параметр перевода: язычок, приклеенный
                    к низу поля адреса; монета и сеть внутри — чипы */}
                <div className={`${s.networkStrip} ${s.tongueBottom}`}>
                    <ShieldAlert size={13} strokeWidth={2.2} aria-hidden="true" />
                    <span dangerouslySetInnerHTML={{ __html: networkInfoHtml }} />
                </div>
            </div>
        </div>
    );
}

// БАНКОВСКИЙ ПЕРЕВОД — самостоятельная «платёжка», а НЕ крипто-раскладка.
// Никаких QR-уголков и огромного логотипа: аккуратная карточка с шапкой
// (небольшой логотип банка + название + чип типа счёта), крупным номером
// счёта с копированием и опциональной строкой владельца. Занимает всю
// ширину блока оплаты — структура под банковский реквизит, не под адрес.
export function BankTransferCard({
    safeT,
    paymentTitle,
    bankName,
    bankLogoSrc,
    accountLabel,
    accountNumber,
    fields,
    isCopied,
    onCopy,
}) {
    const extraFields = (Array.isArray(fields) ? fields : [])
        .map((f) => ({ label: (f?.label || '').trim(), value: (f?.value || '').trim() }))
        .filter((f) => f.label || f.value);

    // Все реквизиты одним потоком: номер — первым (ведущим), затем доп. поля.
    // Пакуются в ряд по ширине контента (flex-wrap); что не влезло — переносится.
    const allFields = [
        { label: accountLabel || '', value: accountNumber || '—', primary: true },
        ...extraFields,
    ];

    // Разделители рисуем по ФАКТУ раскладки: меряем offsetTop каждого поля —
    // поле с новым top начинает ряд (ему — горизонтальный разделитель сверху,
    // без левого вертикального); остальным в ряду — вертикальный слева.
    const sheetRef = useRef(null);
    const [divFlags, setDivFlags] = useState([]);
    useLayoutEffect(() => {
        const el = sheetRef.current;
        if (!el) return undefined;
        const compute = () => {
            const kids = Array.from(el.children);
            if (!kids.length) { setDivFlags([]); return; }
            const tops = kids.map((k) => k.offsetTop);
            const minTop = Math.min(...tops);
            let lastTop = null;
            const next = kids.map((k, i) => {
                const top = tops[i];
                const newRow = lastTop === null || top > lastTop + 1;
                lastTop = top;
                return { left: !newRow, top: top > minTop + 1 };
            });
            setDivFlags(next);
        };
        compute();
        let ro;
        if (typeof ResizeObserver !== 'undefined') {
            ro = new ResizeObserver(compute);
            ro.observe(el);
        }
        return () => { if (ro) ro.disconnect(); };
    }, [accountLabel, accountNumber, extraFields.map((f) => `${f.label}\u0001${f.value}`).join('\u0002')]);
    return (
        <div className={s.bankCard}>
            {/* Шапка-«эмитент»: знак банка + название + кнопка копирования счёта */}
            <header className={s.bankCardHead}>
                <span className={s.bankCardLogo}>
                    {bankLogoSrc
                        ? <BankLogo src={bankLogoSrc} alt={bankName || ''} className={s.bankCardLogoImg} />
                        : <Landmark size={20} strokeWidth={1.8} aria-hidden="true" />}
                </span>
                <span className={s.bankCardHeadText}>
                    <span className={s.bankCardBankName}>{bankName || '—'}</span>
                    {paymentTitle && <span className={s.bankCardHint}>{paymentTitle}</span>}
                </span>
                <button
                    type="button"
                    className={`${s.bankCardCopy} ${isCopied ? s.bankCardCopyDone : ''}`}
                    onClick={onCopy}
                >
                    {isCopied
                        ? <Check size={15} strokeWidth={2.4} aria-hidden="true" />
                        : <Copy size={15} strokeWidth={2} aria-hidden="true" />}
                    <span>
                        {isCopied
                            ? safeT('modals.transactionError.buttons.copied', 'Disalin')
                            : safeT('modals.tax.buttons.copy', 'Salin')}
                    </span>
                </button>
            </header>

            {/* Тело-«выписка»: поля пакуются в ряд по ширине контента; номер —
                ведущее поле (не сжимается, крупный моно). Разделители по факту
                раскладки: вертикальные в ряду, горизонтальные между рядами. */}
            <div className={s.bankCardSheet} ref={sheetRef}>
                {allFields.map((fld, idx) => {
                    const fl = divFlags[idx] || {};
                    const cls = [s.bankCardField];
                    if (fld.primary) cls.push(s.bankCardFieldPrimary);
                    if (fl.left) cls.push(s.bankCardFieldLeftDiv);
                    if (fl.top) cls.push(s.bankCardFieldTopDiv);
                    return (
                        <div className={cls.join(' ')} key={`bankfield-${idx}`}>
                            {fld.label && <span className={s.bankCardLabel}>{fld.label}</span>}
                            {fld.primary
                                ? <code className={s.bankCardNumber} title={fld.value}>{fld.value}</code>
                                : <span className={s.bankCardFieldValue}>{fld.value}</span>}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

// Справка «получатель»: лёгкая таблица 2×2 с разделителями по центру зазоров.
// bare — только сетка, без собственной шапки и отбивки (когда заголовок
// и рамку даёт внешний контекст, например шаг процесса)
export function RecipientGrid({ title, rows, bare }) {
    const grid = (
        <dl className={s.recipientGrid}>
                {rows.map(({ Icon, label, value, logo, mono }, i) => (
                    <div className={s.recipientItem} key={i}>
                        <span className={s.recipientIcon}>
                            {logo
                                ? <BankLogo src={logo} alt="" />
                                : <Icon size={14} strokeWidth={1.9} aria-hidden="true" />}
                        </span>
                        <span className={s.recipientText}>
                            {/* Двоеточие из перевода лишнее: подпись стоит над значением */}
                            <dt className={s.recipientLabel}>
                                {String(label || '').replace(/[::]\s*$/, '')}
                            </dt>
                            <dd className={`${s.recipientValue} ${mono ? s.recipientMono : ''}`}>
                                {value || '—'}
                            </dd>
                        </span>
                    </div>
                ))}
        </dl>
    );

    if (bare) return grid;

    return (
        <div className={s.recipient}>
            <span className={s.eyebrow}>{title}</span>
            {grid}
        </div>
    );
}
